import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { CUSTOMER_VISIBILITIES } from '@lensmira/domain'
import type { CustomerVisibility } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Which branches share customers. §17 `BR-02`, `BR-12`; `later_todo.md` 93.
 *
 * `branch_group` and `branch_group_member` were written by `branch-groups.controller.ts` and by no
 * screen, so a shop could choose "share within groups" in the API and never make a group.
 *
 * ---
 *
 * **The stakes are different from every other setting on the setup screen, and the screen is built
 * around that.** `shared_all` shows every customer and prescription to every branch; `isolated`
 * makes customers vanish from branches that could see them a moment ago. Neither can be taken back
 * for the people affected, so the consequence is stated before the change.
 *
 * **An inline two step rather than a modal or a typed phrase.** Choosing an option changes nothing:
 * it opens a panel that names the effect in full, and only a button inside that panel writes. The
 * button names the outcome a second time ("Share every customer with every branch") instead of
 * saying "Confirm", so the action is read twice in the words of its effect. A modal would trap
 * focus over the very text that has to be read and a typed phrase is a slogan nobody reads, only
 * copies. Betterments over the plain survey version:
 *
 * - the confirm is not focused when the panel opens, so a stray Enter cannot commit it;
 * - choosing the setting the shop already has opens no panel and no button (`alreadySet`);
 * - the confirm is disabled while the tablet is offline, because the panel promises the change
 *   "takes effect straight away" and a queued write would land at an hour nobody chose;
 * - one write in flight, so the panel cannot be pressed twice.
 *
 * ---
 *
 * **The wire values are never shown.** `shared_all`, `shared_group` and `isolated` are what the API
 * takes and refuses anything else with a 422, which would halt the write queue. They live in
 * `CUSTOMER_VISIBILITIES` and every sentence here is the catalogue's.
 *
 * **Customers with no home branch.** `customerVisible` fails closed, so such a customer is invisible
 * under anything but `shared_all`. No read counts them, so the effect sentences say the rule in
 * words and the screen shows no number (`noHomeBranchWarning` is deliberately unused).
 *
 * **A group has at least two branches and cannot be removed.** There is no delete route, so there
 * is no Remove button, and the editor refuses fewer than two itself because the server's 422 would
 * stop the till.
 *
 * **The editor only knows the branches this session holds.** `context.branches` is the caller's own
 * open branches. A group that includes one the owner cannot see is shown, but offered no Change
 * button: the write replaces membership wholesale, so saving it from here would silently drop the
 * members that could not be shown.
 *
 * **`PATCH settings/customer-visibility` takes no `expectedVersion`.** It bumps
 * `tenant_settings.version` regardless, so two owners on stale screens last-write-win. The group
 * write does take one, and a stale save is dropped and reloaded (`staleGroup`).
 */

export interface BranchGroupRow {
  readonly id: string
  readonly name: string
  readonly branchIds: readonly string[]
  readonly version: number
}

export interface CustomerSharingProps {
  /** Appendix B's `tenant_settings_policies`, which all three routes take. Owner alone. */
  readonly canManage: boolean
  /** `context.branches`: the caller's own open branches, which is all the editor can offer. */
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  /** `context.settings.customerVisibility`, the wire value. */
  readonly customerVisibility: string
  /** `GET /branch-groups`, mapped. Not called when `canManage` is false. */
  readonly loadGroups: () => Promise<readonly BranchGroupRow[]>
  /** Called once a write has gone through, so the shell can read the settings again. */
  readonly onWritten?: () => void
}

type Pending =
  | { readonly id: string; readonly kind: 'visibility'; readonly value: CustomerVisibility }
  | { readonly id: string; readonly kind: 'group'; readonly name: string }

export function CustomerSharing({
  canManage,
  branches,
  customerVisibility,
  loadGroups,
  onWritten,
}: CustomerSharingProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot, discard } = useWriteQueue()

  const [groups, setGroups] = useState<readonly BranchGroupRow[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [applied, setApplied] = useState<string | null>(null)
  const [chosen, setChosen] = useState<CustomerVisibility | null>(null)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [name, setName] = useState('')
  const [picked, setPicked] = useState<readonly string[]>([])
  const [nameError, setNameError] = useState<string | null>(null)
  const [branchError, setBranchError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)

  // What the shell last told us, until we have written something newer ourselves.
  useEffect(() => {
    setApplied(null)
  }, [customerVisibility])

  const current = applied ?? customerVisibility

  const refresh = useCallback(() => {
    if (!canManage) return
    loadGroups().then(
      (rows) => {
        setGroups(rows)
        setLoadError(false)
      },
      () => {
        // Its own state: a failed read is not "no groups", and the editor's form errors are not
        // a failed read either.
        setGroups(null)
        setLoadError(true)
      },
    )
  }, [canManage, loadGroups])

  useEffect(() => {
    refresh()
  }, [refresh])

  const optionLabel = (value: CustomerVisibility): string =>
    t(
      value === 'shared_all'
        ? 'customerSharing.optionAll'
        : value === 'shared_group'
          ? 'customerSharing.optionGroup'
          : 'customerSharing.optionOwn',
    )

  useWriteSettled(snapshot, pending?.id ?? null, () => {
    const was = pending
    setPending(null)
    if (was === null) return
    if (was.kind === 'visibility') {
      setApplied(was.value)
      setChosen(null)
      setNotice(t('customerSharing.saved', { how: optionLabel(was.value) }))
    } else {
      setEditing(null)
      setName('')
      setPicked([])
      setNotice(t('customerSharing.groupSaved', { name: was.name }))
    }
    refresh()
    onWritten?.()
  })

  useEffect(() => {
    if (pending === null) return
    const write = snapshot.writes.find((candidate) => candidate.id === pending.id)
    if (write === undefined) {
      // Discarded in `BlockedWrites`: give the screen back rather than freezing it.
      setPending(null)
      return
    }
    if (
      pending.kind === 'group' &&
      write.status === 'blocked' &&
      write.error?.code === 'VERSION_CONFLICT'
    ) {
      // Somebody else saved this group first. The write carries the version that lost, so it can
      // never succeed and would hold the queue. It is this screen's own, so drop it and reload.
      setPending(null)
      setEditing(null)
      setNotice(t('customerSharing.staleGroup'))
      void discard(write.id)
      refresh()
    }
  }, [snapshot, pending, discard, refresh, t])

  const busy = pending !== null
  const groupsReady = groups !== null
  const nameOf = (id: string): string => branches.find((b) => b.id === id)?.name ?? id

  const confirm = useCallback(
    async (value: CustomerVisibility) => {
      // Everything the server would refuse is refused here first: a 409 halts the till.
      if (value === current) return
      if (value === 'shared_group' && (groups === null || groups.length === 0)) return
      setNotice(null)
      const write = (await enqueue({
        method: 'PATCH',
        path: '/settings/customer-visibility',
        label: t('customerSharing.queueLabel'),
        body: { visibility: value },
      })) as { id: string }
      setPending({ id: write.id, kind: 'visibility', value })
    },
    [current, groups, enqueue, t],
  )

  const startEdit = (group: BranchGroupRow | null): void => {
    setNotice(null)
    setEditing(group === null ? 'new' : group.id)
    setName(group === null ? '' : group.name)
    setPicked(group === null ? [] : group.branchIds)
    setNameError(null)
    setBranchError(null)
  }

  const saveGroup = useCallback(async () => {
    const trimmed = name.trim()
    const existing = editing === 'new' ? null : (groups?.find((g) => g.id === editing) ?? null)
    let bad = false
    if (trimmed === '') {
      setNameError(t('customerSharing.groupNeedsName'))
      bad = true
    } else if (groups?.some((g) => g.name === trimmed && g.id !== existing?.id) === true) {
      // `branch_group_name_key`: the server answers 409 and the queue would stop.
      setNameError(t('customerSharing.groupNameTaken', { name: trimmed }))
      bad = true
    } else {
      setNameError(null)
    }
    if (picked.length < 2) {
      setBranchError(t('customerSharing.groupNeedsTwo'))
      bad = true
    } else {
      setBranchError(null)
    }
    if (bad) return
    setNotice(null)
    const write = (await enqueue({
      method: 'PUT',
      path: '/branch-groups',
      label: t('customerSharing.groupQueueLabel', { name: trimmed }),
      body:
        existing === null
          ? { name: trimmed, branchIds: [...picked] }
          : {
              groupId: existing.id,
              name: trimmed,
              branchIds: [...picked],
              expectedVersion: existing.version,
            },
    })) as { id: string }
    setPending({ id: write.id, kind: 'group', name: trimmed })
  }, [name, picked, editing, groups, enqueue, t])

  const form = (
    <div className="lm-setup-panel__form">
      {current === 'shared_group' ? (
        <p className="lm-hint" role="status">
          {t('customerSharing.groupLiveWarning')}
        </p>
      ) : null}
      <div className="lm-field">
        <label className="lm-field__label" htmlFor="sharing-group-name">
          {t('customerSharing.groupName')}
        </label>
        <input
          id="sharing-group-name"
          className="lm-input"
          type="text"
          dir="auto"
          autoComplete="off"
          value={name}
          aria-invalid={nameError !== null}
          onChange={(e) => {
            setName(e.currentTarget.value)
            setNameError(null)
          }}
        />
        {nameError === null ? null : <span className="lm-field__error">{nameError}</span>}
      </div>
      <fieldset
        className="lm-fieldset"
        aria-describedby={branchError === null ? undefined : 'sharing-branch-error'}
      >
        <legend className="lm-field__label">{t('customerSharing.groupBranches')}</legend>
        {branches.map((branch) => (
          <label key={branch.id} className="lm-field">
            <input
              type="checkbox"
              checked={picked.includes(branch.id)}
              onChange={(e) => {
                const on = e.currentTarget.checked
                setPicked((was) =>
                  on ? [...was, branch.id] : was.filter((id) => id !== branch.id),
                )
                setBranchError(null)
              }}
            />
            <span dir="auto">{branch.name}</span>
          </label>
        ))}
        {branchError === null ? null : (
          <span id="sharing-branch-error" className="lm-field__error">
            {branchError}
          </span>
        )}
      </fieldset>
      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={busy}
        onClick={() => {
          void saveGroup()
        }}
      >
        {t('customerSharing.groupSave')}
      </button>
      <button
        type="button"
        className="lm-button lm-button--secondary"
        disabled={busy}
        onClick={() => {
          setEditing(null)
        }}
      >
        {t('customerSharing.keepAsIs')}
      </button>
    </div>
  )

  const line = statusLine(t, snapshot, pending?.id ?? null, t('queue.subject.setup'))

  // The panel for the option picked but not yet confirmed.
  const choiceNeedsGroup = chosen === 'shared_group'
  const groupBlock = !choiceNeedsGroup
    ? null
    : loadError
      ? t('customerSharing.groupsNotLoaded')
      : groups !== null && groups.length === 0
        ? pending?.kind === 'group'
          ? t('customerSharing.needGroupSaved')
          : t('customerSharing.needGroupFirst')
        : null
  const canConfirm =
    chosen !== null &&
    chosen !== current &&
    !busy &&
    snapshot.online &&
    (!choiceNeedsGroup || (groups !== null && groups.length > 0))

  const effectKey =
    chosen === 'shared_all'
      ? 'customerSharing.effectAll'
      : chosen === 'shared_group'
        ? 'customerSharing.effectGroup'
        : 'customerSharing.effectOwn'
  const confirmKey =
    chosen === 'shared_all'
      ? 'customerSharing.confirmAll'
      : chosen === 'shared_group'
        ? 'customerSharing.confirmGroup'
        : 'customerSharing.confirmOwn'

  return (
    <section className="lm-setup-panel" aria-labelledby="sharing-heading">
      <h2 id="sharing-heading" className="lm-setup-panel__heading">
        {t('customerSharing.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('customerSharing.intro')}</p>

      <p className="lm-hint">
        {current === 'shared_all'
          ? t('customerSharing.currentAll')
          : current === 'shared_group'
            ? t('customerSharing.currentGroup')
            : current === 'isolated'
              ? t('customerSharing.currentOwn')
              : t('customerSharing.currentUnknown')}
      </p>

      {canManage ? (
        <>
          <fieldset className="lm-fieldset" aria-labelledby="sharing-heading">
            {CUSTOMER_VISIBILITIES.map((value) => (
              <label key={value} className="lm-field">
                <input
                  type="radio"
                  name="customer-sharing"
                  checked={chosen === value}
                  disabled={busy}
                  onChange={() => {
                    setNotice(null)
                    setChosen(value)
                  }}
                />
                <span>{optionLabel(value)}</span>
              </label>
            ))}
          </fieldset>

          {chosen === null ? null : chosen === current ? (
            <p className="lm-hint" role="status">
              {t('customerSharing.alreadySet')}
            </p>
          ) : (
            <div
              className="lm-surface lm-surface--partial"
              role="group"
              aria-label={optionLabel(chosen)}
            >
              <p>{t(effectKey)}</p>
              {groupBlock === null ? null : (
                <p className="lm-hint" role="status">
                  {groupBlock}
                </p>
              )}
              <p className="lm-hint">{t('customerSharing.confirmHint')}</p>
              <button
                type="button"
                className="lm-button lm-button--primary"
                disabled={!canConfirm}
                onClick={() => {
                  void confirm(chosen)
                }}
              >
                {t(confirmKey)}
              </button>
              <button
                type="button"
                className="lm-button lm-button--secondary"
                disabled={busy}
                onClick={() => {
                  setChosen(null)
                }}
              >
                {t('customerSharing.keepAsIs')}
              </button>
            </div>
          )}
        </>
      ) : null}

      <h3 className="lm-setup-panel__heading">{t('customerSharing.groupsHeading')}</h3>
      <p className="lm-setup-panel__effects">{t('customerSharing.groupsMeans')}</p>

      <DataSurface
        state={
          !canManage
            ? { kind: 'denied' }
            : loadError
              ? { kind: 'error', message: t('customerSharing.groupsReadFailed'), onRetry: refresh }
              : groups === null
                ? { kind: 'loading' }
                : groups.length === 0
                  ? { kind: 'empty' }
                  : { kind: 'ready' }
        }
        shape="rows"
        rows={2}
        what={t('customerSharing.groupsHeading')}
        emptyMessage={t('customerSharing.groupsEmpty')}
        deniedMessage={t('customerSharing.groupsDenied')}
      >
        <ul className="lm-supplier-list">
          {(groups ?? []).map((group) => {
            const known = group.branchIds.filter((id) => branches.some((b) => b.id === id))
            const hidden = group.branchIds.length - known.length
            return (
              <li key={group.id} className="lm-supplier">
                <span className="lm-supplier__name" dir="auto">
                  {group.name}
                </span>
                <span className="lm-supplier__meta">
                  {t('customerSharing.groupMembers', {
                    count: group.branchIds.length,
                    names: known.map(nameOf).join(', '),
                  })}
                </span>
                {hidden > 0 ? (
                  <span className="lm-supplier__meta">
                    {t('customerSharing.groupsPartial', { count: hidden })}
                  </span>
                ) : null}
                {hidden > 0 ? (
                  <span className="lm-hint">{t('customerSharing.groupLocked')}</span>
                ) : editing === group.id ? (
                  form
                ) : (
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={busy}
                    onClick={() => {
                      startEdit(group)
                    }}
                  >
                    {t('customerSharing.groupEdit')}
                    <span className="lm-visually-hidden"> {group.name}</span>
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </DataSurface>

      {canManage && groupsReady ? (
        editing === 'new' ? (
          form
        ) : (
          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={busy}
            onClick={() => {
              startEdit(null)
            }}
          >
            {t('customerSharing.groupAdd')}
          </button>
        )
      ) : null}

      {notice === null ? null : (
        <p className="lm-hint" role="status">
          {notice}
        </p>
      )}
      {canManage ? <p className="lm-hint">{line}</p> : null}
    </section>
  )
}
